/** @jsxImportSource @emotion/react */
/**
 * LocationBreadcrumb — отображает путь расположения посадки.
 * Упрощает путь: если ферма/поле/участок в единственном экземпляре — узел скрывается.
 * Пример: одна ферма/поле/участок → "Грядка A1B2"
 *         несколько участков → "Участок: Яблони → Грядка: A1B2"
 */
import React from 'react';
import { Typography } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import type { PlantingLocation, LocationContext } from '../../../models/Planting';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatBedDisplayName } from '../../../utils/bedDisplayName';

const { Text } = Typography;

const styles = {
  container: css({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  }),
  icon: css({
    color: '#52c41a',
    fontSize: 14,
  }),
  separator: css({
    color: 'rgba(0,0,0,0.25)',
    margin: '0 2px',
  }),
  segment: css({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  }),
  label: css({
    color: 'rgba(0,0,0,0.45)',
    fontSize: 13,
  }),
  value: css({
    fontSize: 13,
  }),
};

interface LocationBreadcrumbProps {
  location?: PlantingLocation | null;
  locationContext?: LocationContext;
  /** If true, renders as links to entities */
  linkable?: boolean;
}

/**
 * Формирует имя грядки: пользовательское имя или anchor_code
 */
const getBedDisplayName = (location: PlantingLocation): string => {
  const bed = location.bed;
  if (!bed) return '—';
  return formatBedDisplayName(bed.name, bed.anchor_code, bed.id);
};

const LocationBreadcrumb: React.FC<LocationBreadcrumbProps> = ({
  location,
  locationContext,
  linkable = false,
}) => {
  const { t } = useTranslation('plantings');
  const { saveId } = useParams<{ saveId: string }>();
  if (!location) {
    return (
      <Text type="secondary">
        <EnvironmentOutlined css={styles.icon} /> {t('breadcrumbLocation.notSet')}
      </Text>
    );
  }

  const showFarm = (locationContext?.farmsTotal ?? 0) > 1 && location.farm?.name;
  const showField = (locationContext?.fieldsTotal ?? 0) > 1 && location.field?.name;
  const showPlot = (locationContext?.plotsTotal ?? 0) > 1 && location.plot?.name;
  const bedName = getBedDisplayName(location);

  const segments: React.ReactNode[] = [];

  if (showFarm) {
    segments.push(
      <span key="farm" css={styles.segment}>
        <Text type="secondary" css={styles.label}>{t('breadcrumbLocation.farm')}</Text>
        {linkable && saveId ? (
          <Link to={`/saves/${saveId}`}>
            <Text css={styles.value}>{location.farm.name}</Text>
          </Link>
        ) : (
          <Text css={styles.value}>{location.farm?.name}</Text>
        )}
      </span>
    );
  }

  if (showField) {
    segments.push(
      <span key="field" css={styles.segment}>
        <Text type="secondary" css={styles.label}>{t('breadcrumbLocation.field')}</Text>
        {linkable && saveId && location.field?.id ? (
          <Link to={`/saves/${saveId}/fields/${location.field.id}/info`}>
            <Text css={styles.value}>{location.field.name}</Text>
          </Link>
        ) : (
          <Text css={styles.value}>{location.field?.name}</Text>
        )}
      </span>
    );
  }

  if (showPlot) {
    segments.push(
      <span key="plot" css={styles.segment}>
        <Text type="secondary" css={styles.label}>{t('breadcrumbLocation.plot')}</Text>
        {linkable && saveId && location.plot?.id ? (
          <Link to={`/saves/${saveId}/plots/${location.plot.id}/plan`}>
            <Text css={styles.value}>{location.plot.name}</Text>
          </Link>
        ) : (
          <Text css={styles.value}>{location.plot?.name}</Text>
        )}
      </span>
    );
  }

  // Bed is always shown
  segments.push(
    <span key="bed" css={styles.segment}>
      {segments.length > 0 && (
        <Text type="secondary" css={styles.label}>{t('breadcrumbLocation.bed')}</Text>
      )}
      {linkable && saveId && location.bed?.id ? (
        <Link to={`/saves/${saveId}/beds/${location.bed.id}`}>
          <Text css={styles.value}>{bedName}</Text>
        </Link>
      ) : segments.length === 0 ? (
        <Text css={styles.value}>{bedName}</Text>
      ) : (
        <Text css={styles.value}>{bedName}</Text>
      )}
    </span>
  );

  return (
    <span css={styles.container}>
      <EnvironmentOutlined css={styles.icon} />
      {segments.map((segment, i) => (
        <React.Fragment key={i}>
          {i > 0 && <Text css={styles.separator}>→</Text>}
          {segment}
        </React.Fragment>
      ))}
    </span>
  );
};

export default LocationBreadcrumb;
